// CabinetOrient — conversation AYA → Orientation recommandée — MAQUETTE exacte (v5.22.0).
// Bulles horodatées · carte orientation (fond crème, icône orange, Confiance élevée) ·
// disclaimer 💡 · actions · Questions fréquentes · barre de saisie (trombone + envoi rond).
import { useRef, useState } from "react";
import axios from "axios";
import { Banknote, CalendarCheck, FileText, Home, Paperclip, Send, Video } from "lucide-react";
import { toast } from "sonner";
import { T, PresenceDot } from "./cabinetTheme";

const API = process.env.REACT_APP_BACKEND_URL;
const ts = () => new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const FAQ = [
  { Icon: FileText, bg: "#DBEAFE", fg: "#1D4ED8", q: "Que faire en cas de litige locatif ?" },
  { Icon: Banknote, bg: "#DCFCE7", fg: "#0E8A4F", q: "Comment récupérer ma caution ?" },
  { Icon: Home, bg: "#FFEDD5", fg: "#EA580C", q: "Résiliation de bail" },
];

const AyaAvatar = () => (
  <span style={{ position: "relative", flexShrink: 0, marginTop: 2 }}>
    <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#0B1324", color: "#fff", fontSize: 7.5, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", letterSpacing: 0.5 }}>AYA</span>
    <PresenceDot size={8} style={{ position: "absolute", bottom: -1, right: -1, border: "1.5px solid #fff" }} />
  </span>
);

export const CabinetOrient = ({ slug, source, onPickInterlocutor, onBook }) => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! Je suis AYA, je vous aide à entrer en contact avec le cabinet. Pouvez-vous m'expliquer brièvement votre situation ?", at: ts() },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [qualif, setQualif] = useState(null);
  const endRef = useRef(null);

  const send = async (text) => {
    const msg = String(text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.slice(1).map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: msg, at: ts() }]);
    setInput(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/api/v3/cabinet/${slug}/orient`, { message: msg, history, source });
      setMessages((prev) => [...prev, { role: "assistant", content: r.data.reply, at: ts() }]);
      if (r.data.qualified && r.data.qualification) setQualif(r.data.qualification);
    } catch {
      toast.error("AYA est momentanément indisponible");
    } finally {
      setLoading(false);
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, minHeight: "72vh" }} data-testid="cabinet-orient">
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto" }} data-testid="orient-messages">
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 8, justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            {m.role === "assistant" && <AyaAvatar />}
            <div style={{
              maxWidth: "82%", padding: "10px 13px", fontSize: 13.5, lineHeight: 1.5,
              ...(m.role === "user"
                ? { background: "#065F46", color: "#fff", borderRadius: "15px 15px 4px 15px" }
                : { background: "#F3F4F6", color: "#111827", borderRadius: "15px 15px 15px 4px" }),
            }} data-testid={`orient-msg-${m.role}-${i}`}>
              {m.content}
              <span style={{ display: "block", textAlign: "right", fontSize: 9.5, marginTop: 3, opacity: 0.6 }}>
                {m.at}{m.role === "user" ? " ✓✓" : ""}
              </span>
            </div>
          </div>
        ))}
        {loading && <div style={{ ...T.sub, fontSize: 11.5, paddingLeft: 36 }}>AYA analyse votre situation…</div>}

        {qualif && (
          <>
            <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 16, padding: "14px 15px" }} data-testid="orient-qualification">
              <div style={{ fontSize: 14, fontWeight: 900, color: "#111827" }}>Orientation recommandée</div>
              <div style={{ display: "flex", gap: 12, alignItems: "center", margin: "11px 0 9px" }}>
                <div style={{ width: 50, height: 50, borderRadius: "50%", background: "#FB923C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Home size={23} color="#7C2D12" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.15 }} data-testid="orient-domain">{qualif.domain}</div>
                  <div style={{ ...T.sub, fontSize: 12.5 }}>{qualif.type}</div>
                </div>
                <span style={{ ...T.badge, background: "#DCFCE7", color: "#0E8A4F", flexShrink: 0 }}>✓ Confiance élevée</span>
              </div>
              <div style={{ display: "grid", gap: 6, margin: "4px 0 11px" }}>
                {(qualif.elements?.length ? qualif.elements.slice(0, 4) : ["Analyse de votre situation", "Explication de vos droits", "Prochaines étapes possibles"]).map((e, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: "#374151" }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#0E8A4F", flexShrink: 0, marginTop: 5 }} />{e}
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#ECFDF5", borderRadius: 10, padding: "9px 11px" }}>
                <span style={{ fontSize: 13, flexShrink: 0 }}>💡</span>
                <span style={{ fontSize: 11.5, color: "#374151", lineHeight: 1.45 }}>Cette orientation est indicative. Un professionnel du droit pourra analyser votre situation plus précisément.</span>
              </div>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <button style={{ ...T.btnGreen, width: "100%", padding: "13px 16px", borderRadius: 12 }} onClick={() => onPickInterlocutor(qualif)} data-testid="orient-pick-interlocutor">
                <Video size={16} /> Parler à un interlocuteur
              </button>
              <button style={{ ...T.btnOutline, width: "100%", padding: "12px 16px", borderRadius: 12 }} onClick={() => onBook(qualif)} data-testid="orient-book">
                <CalendarCheck size={15} /> Prendre rendez-vous
              </button>
            </div>
          </>
        )}

        {/* Questions fréquentes */}
        <div style={{ marginTop: 2 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 900, color: "#111827" }}>Questions fréquentes</h3>
            <span style={{ color: "#0E8A4F", fontSize: 12, fontWeight: 800 }}>Voir plus →</span>
          </div>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", marginTop: 9 }} data-testid="orient-faq">
            {FAQ.map((f, i) => (
              <button key={i} onClick={() => send(f.q)} data-testid={`orient-faq-${i}`}
                style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, maxWidth: 190, textAlign: "left", padding: "9px 11px", borderRadius: 12, border: "1px solid #E5E7EB", background: "#fff", cursor: "pointer" }}>
                <span style={{ width: 28, height: 28, borderRadius: 9, background: f.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <f.Icon size={14} color={f.fg} />
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>{f.q}</span>
              </button>
            ))}
          </div>
        </div>
        <div ref={endRef} />
      </div>

      {/* Barre de saisie : trombone · champ · envoi rond vert */}
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={() => toast.info("Pièces jointes bientôt disponibles")} data-testid="orient-attach" aria-label="Joindre un fichier"
          style={{ width: 40, height: 40, borderRadius: "50%", border: "1px solid #E5E7EB", background: "#fff", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <Paperclip size={16} />
        </button>
        <input style={{ ...T.input, flex: 1, background: "#F3F4F6", border: "1px solid #E5E7EB" }} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Écrire un message…" data-testid="orient-input" />
        <button onClick={() => send()} disabled={loading} data-testid="orient-send" aria-label="Envoyer"
          style={{ width: 42, height: 42, borderRadius: "50%", border: "none", background: "#0E8A4F", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, opacity: loading ? 0.6 : 1 }}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
};
